import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { DragIndicator } from "@material-ui/icons";
import { Draggable } from "@hello-pangea/dnd";
import styled from "styled-components";
import {
  checkNumberLength,
  checkNumberLengthAfterDecimal,
  extractNumericValue,
  isValidNumberStringValues,
  mapCurrencyValue,
  mapOverheadCurrencyValue,
  roundToDecimals,
} from "../../utils/helpers";
import {
  FIELD_TYPES,
  VISIT_CATEGORY_TYPES,
  VISIT_COUNTRY_COLUMNS_WIDTH,
  VISIT_STUDY_COLUMNS_WIDTH,
  VISIT_VARIANCE_TYPE_OPTIONS,
} from "../constants";
import VisitInputField from "./visit.input.field";
import RemoveButton from "../../../../app/components/buttons/remove";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";
import { getCommentType, getCommentWidth } from "./table.utils";

export default function TableRow({
  sv,
  removeRow,
  onVisitsChange,
  value,
  index,
  fieldType,
  siteId,
  isCountry,
  currency,
  readonly,
}) {
  const timeoutIds = useRef({});

  const commentTypeKey = React.useMemo(() => {
    if (fieldType === FIELD_TYPES.VISIT) return "commentsVisit";
    if (fieldType === FIELD_TYPES.COST) return "commentsCost";
    if (fieldType === FIELD_TYPES.MEDICAL) return "commentsClinicalStrategy";
    if (fieldType === FIELD_TYPES.DATA_MANAGEMENT)
      return "commentsDataManagement";
    return "";
  }, [fieldType]);

  // Get the display name for the comment type
  const commentDisplayType = React.useMemo(() => {
    return getCommentType(fieldType);
  }, [fieldType]);

  useEffect(() => {
    setCommentTypeKeyState(sv[commentTypeKey] || "");
  }, [fieldType, commentTypeKey, sv]);

  const [commentTypeKeyState, setCommentTypeKeyState] = useState(sv?.[commentTypeKey] || "");
  const [visitVarianceState, setVisitVarianceState] = useState(sv?.visitVariance || "");
  const [visitStudyDayState, setVisitStudyDayState] = useState(sv?.visitStudyDay || 0);
  const [visitCostTotalState, setVisitCostTotalState] = useState(sv?.visitCostTotal || "");
  const [isEditingCost, setIsEditingCost] = useState(false);

  useEffect(() => {
    setVisitCostTotalState(sv.visitCostTotal || "");
  }, [sv.visitCostTotal]);

  const renderVisitCostTotal = () => {
    const isCountryLevel = siteId === 1;
    const isReadOnly = isCountryLevel || readonly;

    if (isReadOnly) {
      return (
        <CustomTooltip title={mapCurrencyValue(currency, visitCostTotalState)}>
          <Typography variant="body1">{mapCurrencyValue(currency, visitCostTotalState)}</Typography>
        </CustomTooltip>
      );
    }

    return (
      <CustomTooltip title={isEditingCost ? visitCostTotalState : mapCurrencyValue(currency, visitCostTotalState)}>
        <TextField
          style={{ width: "100%" }}
          value={isEditingCost ? visitCostTotalState : mapCurrencyValue(currency, visitCostTotalState)}
          onFocus={() => {
            if (readonly) return;
            setIsEditingCost(true);
          }}
          onChange={(e) => {
            if (readonly) return;
            const inputValue = e.target.value;
            const numericValue = extractNumericValue(inputValue);
            setVisitCostTotalState(numericValue);
          }}
          onBlur={(e) => {
            if (readonly) return;
            setIsEditingCost(false);
            const inputValue = e.target.value.trim();

            if (inputValue === "") {
              setVisitCostTotalState("");
              debouncedHandlers.visitCostTotal("0");
              return;
            }

            let numericValue = extractNumericValue(inputValue);
            if (!checkNumberLengthAfterDecimal(numericValue, 2)) {
              numericValue = roundToDecimals(numericValue, 2);
            }
            if (isValidNumberStringValues(numericValue) &&
              checkNumberLength(numericValue, 8, 2)
            ) {
              setVisitCostTotalState(numericValue);
              debouncedHandlers.visitCostTotal(numericValue);
            }
          }}
          variant="outlined"
          size="small"
          disabled={readonly}
          InputProps={{ readOnly: readonly }}
        />
      </CustomTooltip>
    );
  };
  const debounce = useCallback(
    (func, wait, fieldKey) => {
      return function (...args) {
        const timeoutKey = `${
          sv.scheduleOfEventsVisitId || sv.key
        }_${fieldKey}`;

        if (timeoutIds.current[timeoutKey]) {
          clearTimeout(timeoutIds.current[timeoutKey]);
        }

        timeoutIds.current[timeoutKey] = setTimeout(() => {
          timeoutIds.current[timeoutKey] = null;
          func.apply(null, args);
        }, wait);
      };
    },
    [sv.scheduleOfEventsVisitId, sv.key],
  );

  // Create separate debounced handlers for different fields
  const debouncedHandlers = {
    visitStudyDay: useCallback(
      debounce(
        (value) =>
          onVisitsChange(
            "visitStudyDay",
            value,
            sv.scheduleOfEventsVisitId || sv.key,
          ),
        500,
        "visitStudyDay",
      ),
      [debounce, onVisitsChange, sv.scheduleOfEventsVisitId, sv.key],
    ),
    visitVariance: useCallback(
      debounce(
        (value) =>
          onVisitsChange(
            "visitVariance",
            value,
            sv.scheduleOfEventsVisitId || sv.key,
          ),
        500,
        "visitVariance",
      ),
      [debounce, onVisitsChange, sv.scheduleOfEventsVisitId, sv.key],
    ),
    comments: useCallback(
      debounce(
        (value) =>
          onVisitsChange(
            commentTypeKey,
            value,
            sv.scheduleOfEventsVisitId || sv.key,
          ),
        500,
        "comments",
      ),
      [debounce, onVisitsChange, commentTypeKey, sv.scheduleOfEventsVisitId, sv.key],
    ),
    visitCostTotal: useCallback(
      debounce(
        (value) => {
          const parsedValue = value !== "" ? parseFloat(value) : null;
          onVisitsChange(
            "visitCostTotal",
            parsedValue,
            sv.scheduleOfEventsVisitId || sv.key,
          );
        },
        500,
        "visitCostTotal",
      ),
      [debounce, onVisitsChange, sv.scheduleOfEventsVisitId, sv.key],
    ),
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    const currentTimeouts = timeoutIds.current;
    return () => {
      Object.values(currentTimeouts).forEach((timeoutId) => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, []);

  // Get column widths based on context (country vs study, expanded vs collapsed)
  const widths = useMemo(() => {
    const config = isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH : VISIT_STUDY_COLUMNS_WIDTH;
    const mode = value === 0 ? 'expanded' : 'collapsed';
    
    return {
      visitCategory: config.visitCategory[mode],
      visitName: config.visitName[mode],
      studyDay: config.studyDay[mode],
      variance: config.variance[mode],
      visitWindow: config.visitWindow[mode],
      overheadPct: isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH.overheadPct[mode] : null,
      overhead: isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH.overhead[mode] : null,
      totalVisitCost: isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH.totalVisitCost[mode] : null,
      deleteButton: config.deleteButton,
    };
  }, [isCountry, value]);

  const renderRow = (dndProps) => {
    const { dragHandleProps, ...remainingDnDProps } = dndProps;
    
    // Calculate comment width using utility function
    const commentWidth = getCommentWidth(commentDisplayType, readonly);
    
    return (
      <Row {...remainingDnDProps}>
        <Cell width={widths.visitCategory}>
          {value === 0 ? (
            <CellContent>
              <DragHandleContainer {...(readonly ? {} : dragHandleProps)}>
                <DragIndicator style={{ color: readonly ? '#ccc' : '#555' }} />
              </DragHandleContainer>
              <SelectContainer>
                <CustomTooltip title={sv.visitCategory || ''}>
                  <FormControl
                    style={{ width: "100%" }}
                    size="small"
                    variant="outlined"
                  >
                    <Select
                      value={sv.visitCategory}
                      onChange={(e) => {
                        if (readonly) return;
                        onVisitsChange(
                          "visitCategory",
                          e.target.value,
                          sv.scheduleOfEventsVisitId || sv.key,
                          "string",
                        );
                      }}
                      displayEmpty
                      disabled={readonly}
                      style={{
                        width: "100%",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {VISIT_CATEGORY_TYPES.map((vc) => (
                        <MenuItem
                          value={vc}
                          key={vc}
                        >
                          {vc}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CustomTooltip>
              </SelectContainer>
            </CellContent>
          ) : (
            <CustomTooltip title={sv.visitCategory || ''}>
              <Typography
                variant="body1"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  padding: "5px",
                }}
              >
                {sv.visitCategory}
              </Typography>
            </CustomTooltip>
          )}
        </Cell>
        <Cell width={widths.visitName}>
          {value === 0 ? (
            <CustomTooltip title={sv.visitName || ''}>
              <div style={{ width: '100%' }}>
                <VisitInputField
                  onVisitsChange={onVisitsChange}
                  sv={sv}
                  readonly={readonly}
                />
              </div>
            </CustomTooltip>
          ) : (
            <CustomTooltip title={sv.visitName || ''}>
              <Typography variant="body1">{sv.visitName}</Typography>
            </CustomTooltip>
          )}
        </Cell>
        <Cell width={widths.studyDay}>
          {value === 0 ? (
            <CustomTooltip title={visitStudyDayState?.toString() || ''}>
              <TextField
                style={{ width: "100%" }}
                value={visitStudyDayState}
                onChange={(e) => {
                  if (readonly) return;
                  if (
                    !isValidNumberStringValues(e.target.value) ||
                    !checkNumberLength(e.target.value, 4, 0, 9999)
                  )
                    return;

                  setVisitStudyDayState(e.target.value);
                  debouncedHandlers.visitStudyDay(e.target.value);
                }}
                variant="outlined"
                size="small"
                disabled={readonly || sv.visitCategory === "Unscheduled"}
                InputProps={{ readOnly: readonly }}
              />
            </CustomTooltip>
          ) : (
            <CustomTooltip title={sv.visitStudyDay?.toString() || ''}>
              <Typography variant="body1">{sv.visitStudyDay}</Typography>
            </CustomTooltip>
          )}
        </Cell>

        <Cell width={widths.variance}>
          {value === 0 ? (
            <CustomTooltip title={
              VISIT_VARIANCE_TYPE_OPTIONS.find(vvt => vvt.value === sv.visitVarianceType)?.label || ''
            }>
              <FormControl
                style={{ width: "100%" }}
                size="small"
                variant="outlined"
              >
                <Select
                  id="visit-variance-type"
                  value={sv.visitVarianceType}
                  onChange={(e) => {
                    if (readonly) return;
                    onVisitsChange(
                      "visitVarianceType",
                      e.target.value,
                      sv.scheduleOfEventsVisitId || sv.key,
                      "string",
                    );
                  }}
                  disabled={readonly}
                >
                  {VISIT_VARIANCE_TYPE_OPTIONS.map((vvt) => (
                    <MenuItem
                      value={vvt.value}
                      key={vvt.value}
                    >
                      {vvt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CustomTooltip>
          ) : (
            <CustomTooltip title={sv.visitVarianceType || ''}>
              <Typography variant="body1">{sv.visitVarianceType}</Typography>
            </CustomTooltip>
          )}
        </Cell>
        <Cell width={widths.visitWindow}>
          {value === 0 ? (
            <CustomTooltip title={visitVarianceState?.toString() || ''}>
              <TextField
                style={{ width: "100%" }}
                value={visitVarianceState}
                onChange={(e) => {
                  if (readonly) return;
                  if (!isValidNumberStringValues(e.target.value) ||
                    !checkNumberLength(e.target.value, 5, 0, 9999)) return;

                  setVisitVarianceState(e.target.value);
                  debouncedHandlers.visitVariance(e.target.value);
                }}
                variant="outlined"
                size="small"
                disabled={readonly}
                InputProps={{ readOnly: readonly }}
              />
            </CustomTooltip>
          ) : (
            <CustomTooltip title={sv.visitVariance?.toString() || ''}>
              <Typography variant="body1">{sv.visitVariance}</Typography>
            </CustomTooltip>
          )}
        </Cell>

        {(fieldType === FIELD_TYPES.COST || value !== 0) && isCountry && (
          <>
            <Cell width={widths.overheadPct}>
              <CustomTooltip title={(sv.visitOverheadPct ?? "").toString()}>
                <TextField
                  style={{ width: "100%" }}
                  value={sv.visitOverheadPct ?? ""}
                  onChange={(e) => {
                    if (readonly) return;
                    const value = e.target.value;

                    if (value.startsWith("-")) return;

                    if (
                      !isValidNumberStringValues(value) ||
                      !checkNumberLength(value, 3, 0, 100)
                    )
                      return;

                    onVisitsChange(
                      "visitOverheadPct",
                      value,
                      sv.scheduleOfEventsVisitId || sv.key,
                    );
                  }}
                  variant="outlined"
                  size="small"
                  disabled={readonly}
                  InputProps={{ readOnly: readonly }}
                />
              </CustomTooltip>
            </Cell>
            <Cell width={widths.overhead}>
              <CustomTooltip title={mapOverheadCurrencyValue(currency, sv.visitCost)}>
                <Typography variant="body1">{mapOverheadCurrencyValue(currency, sv.visitCost)}</Typography>
              </CustomTooltip>
            </Cell>
            <Cell width={widths.totalVisitCost}>
              {renderVisitCostTotal()}
            </Cell>
          </>
        )}
        {value === 0 && (
          <>
            {fieldType === FIELD_TYPES.MEDICAL && (
              <>
                <Cell width="6%">
                  <CustomTooltip title={(sv.miEstSDRSDV ?? "").toString()}>
                    <TextField
                      style={{ width: "100%" }}
                      value={sv.miEstSDRSDV ?? ""}
                      onChange={(e) => {
                        if (readonly) return;
                        const value = e.target.value;

                        if (value.startsWith("-")) return;

                        if (!isValidNumberStringValues(value) ||
                          !checkNumberLength(value, 6, 2)) return;

                        onVisitsChange(
                          "miEstSDRSDV",
                          value,
                          sv.scheduleOfEventsVisitId || sv.key,
                        );
                      }}
                      variant="outlined"
                      size="small"
                      disabled={readonly}
                      InputProps={{ readOnly: readonly }}
                    />
                  </CustomTooltip>
                </Cell>
                <Cell width="6%">
                  <CustomTooltip title={Boolean(sv.miSampleVisit) ? 'Yes' : 'No'}>
                    <div>
                      <Checkbox
                        checked={Boolean(sv.miSampleVisit)}
                        onChange={(e) => {
                          if (readonly) return;
                          onVisitsChange(
                            "miSampleVisit",
                            e.target.checked,
                            sv.scheduleOfEventsVisitId || sv.key,
                            "checkbox",
                          );
                        }}
                        name="detailsMonthly"
                        disabled={readonly}
                      />
                    </div>
                  </CustomTooltip>
                </Cell>
                <Cell width="6%">
                  <CustomTooltip title={sv.miSuggestedOnSiteSDRSDV?.toString() || ''}>
                    <Typography variant="body1">
                      {sv.miSuggestedOnSiteSDRSDV}
                    </Typography>
                  </CustomTooltip>
                </Cell>
                <Cell width="6%">
                  <CustomTooltip title={sv.miSuggestedCDAReview?.toString() || ''}>
                    <Typography variant="body1">
                      {sv.miSuggestedCDAReview}
                    </Typography>
                  </CustomTooltip>
                </Cell>
              </>
            )}

            {fieldType === FIELD_TYPES.DATA_MANAGEMENT && (
              <>
              <Cell width="6%">
                <CustomTooltip title={(sv.dmUniqueCRFs ?? "").toString()}>
                  <Typography variant="body1">
                    {sv.dmUniqueCRFs != null && sv.dmUniqueCRFs !== "" 
                      ? parseFloat(sv.dmUniqueCRFs).toFixed(2) 
                      : ""}
                  </Typography>
                </CustomTooltip>
              </Cell>
              <Cell width="6%">
                <CustomTooltip title={(sv.dmNonUniqueCRFs ?? "").toString()}>
                  <Typography variant="body1">
                    {sv.dmNonUniqueCRFs != null && sv.dmNonUniqueCRFs !== "" 
                      ? parseFloat(sv.dmNonUniqueCRFs).toFixed(2) 
                      : ""}
                  </Typography>
                </CustomTooltip>
              </Cell>
              </>
            )}
          </>
        )}
        {value === 0 && (
          <Cell width={commentWidth}>
            <CustomTooltip title={commentTypeKeyState || ''}>
              <TextField
                key={commentTypeKey}
                value={commentTypeKeyState}
                onChange={(e) => {
                  if (readonly) return;
                  setCommentTypeKeyState(e.target.value);
                  debouncedHandlers.comments(e.target.value);
                }}
                variant="outlined"
                size="small"
                inputProps={{
                  maxLength: 250,
                }}
                style={{ width: "100%" }}
                disabled={readonly}
                InputProps={{ readOnly: readonly }}
              />
            </CustomTooltip>
          </Cell>
        )}
        {(!readonly && value === 0) && (
          <Cell width={widths.deleteButton}>
            <CustomTooltip title="Remove row">
              <span>
                <RemoveButton
                  onClick={() => {
                    if (readonly) return;
                    removeRow(sv.scheduleOfEventsVisitId || sv.key);
                  }}
                  disabled={readonly}
                />
              </span>
            </CustomTooltip>
          </Cell>
        )}
      </Row>
    );
  };

  return (
    <>
      {value === 0 ? (
        <Draggable
          draggableId={(sv.key || sv.scheduleOfEventsVisitId).toString()}
          index={index}
          isDragDisabled={readonly}
        >
          {(provided) =>
            renderRow({
              dragHandleProps: readonly ? {} : provided.dragHandleProps,
              ...provided.draggableProps,
              ref: provided.innerRef,
            })
          }
        </Draggable>
      ) : (
        renderRow({})
      )}
    </>
  );
}

const DragHandleContainer = styled.div`
  color: #555;
  width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Row = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  min-height: 50px;
  border-bottom: ${(p) =>
    p.noBorder ? "none" : "1px solid var(--surface-300)"};
  
  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-100)" : "transparent"};
  height: 100%;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => (p.width ? p.width : "100%")};
  border-right: 1px solid var(--surface-300);
  padding-inline: 5px;
  text-align: center;
  border-left: ${(p) =>
    p.leftBorder ? "1px solid var(--surface-300)" : "none"};
  &:last-child {
    border-right: ${(p) => (p.rightBorder ? "1px solid var(--surface-300)" : "none")};
  }
  border: ${(p) => (p.fullBorder ? "1px solid var(--surface-300)" : "")};
  
  /* All direct children fill width and height, maintain vertical centering */
  > * {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
  }
  
  /* Text elements - centered */
  .MuiTypography-root {
    justify-content: center;
  }
  
  /* Input elements - fill width, left-aligned text */
  .MuiTextField-root,
  .MuiFormControl-root {
    width: 100% !important;
    
    .MuiInputBase-root {
      width: 100%;
    }
    
    .MuiInputBase-input {
      text-align: left;
    }
  }
  
  /* Elements that should maintain natural size */
  .MuiCheckbox-root,
  button {
    width: auto !important;
    justify-content: center;
  }
`;

const SelectContainer = styled.div`
  flex: 1;
  min-width: 0;
  padding: 5px;
  overflow: hidden;
  display: flex;
  align-items: center;
  height: 100%;
`;

const CellContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 5px;
`;
